import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { chunkingStrategies } from "@/lib/knowledgeConstants";
import { cn } from "@/lib/utils";
import {
  Search, FileText, Sparkles, Database, RefreshCw, Loader2, BookOpen, Cog, Mic, Video,
  Cloud, Upload, Globe, Building2, AlertCircle, Box,
} from "lucide-react";
import { toast } from "sonner";
import { BrainNav } from "@/components/brain/BrainNav";
import { SourceStatusBadge } from "@/components/brain/SourceStatusBadge";
import { CustomSourceManager } from "@/components/brain/CustomSourceManager";
import { useBrainOverview } from "@/hooks/useBrainOverview";
import { useAssistantName } from "@/hooks/useAssistantName";
import { brainApi } from "@/lib/api";
import { BRAIN_EXTENDED_FEATURES_ENABLED } from "@/lib/featureFlags";
import { formatDateTime } from "@/lib/format";

const SOURCE_ICONS = {
  sharepoint: { Icon: Building2, color: "bg-blue-600" },
  google_drive: { Icon: Cloud, color: "bg-emerald-600" },
  onedrive: { Icon: Cloud, color: "bg-sky-500" },
  dropbox: { Icon: Box, color: "bg-blue-500" },
  file_upload: { Icon: Upload, color: "bg-violet-500" },
  custom: { Icon: Upload, color: "bg-violet-500" },
  url_crawl: { Icon: Globe, color: "bg-amber-500" },
  notion: { Icon: FileText, color: "bg-slate-700" },
  confluence: { Icon: FileText, color: "bg-indigo-600" },
  crm: { Icon: Database, color: "bg-indigo-500" },
  voice_memos: { Icon: Mic, color: "bg-indigo-500" },
  meeting_recordings: { Icon: Video, color: "bg-emerald-600" },
};

const DEFERRED_KNOWLEDGE_SOURCES = {
  voice_memos: {
    id: "voice_memos",
    name: "Voice memos",
    description: "Record from the Sellyx mobile app.",
    status: "not_configured",
    status_label: "Not Configured",
    documents: 0,
    last_sync: "—",
    connected: false,
    configured: false,
  },
  meeting_recordings: {
    id: "meeting_recordings",
    name: "Meeting recordings",
    description: "Calendar-linked meeting transcripts.",
    status: "not_configured",
    status_label: "Not Configured",
    documents: 0,
    last_sync: "—",
    connected: false,
    configured: false,
  },
};

function KpiTile({ label, value, hint }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="text-[10.5px] font-medium uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-1 text-[22px] font-semibold tracking-tight text-slate-900 tabular-nums">{value}</div>
      {hint && <div className="mt-0.5 text-[10.5px] text-slate-500">{hint}</div>}
    </div>
  );
}
KpiTile.propTypes = { label: PropTypes.string.isRequired, value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired, hint: PropTypes.string };

function BrainEmptyBanner() {
  return (
    <div
      className="rounded-xl border border-slate-200 bg-slate-50/80 px-4 py-3 text-[13px] text-slate-700"
      data-testid="brain-empty-banner"
    >
      <div className="font-semibold text-slate-900">No knowledge sources connected yet.</div>
      <p className="mt-1 text-slate-600">
        Use <strong>Set up</strong> on a knowledge source below to upload documents or connect a repository.
        Communication channels (Gmail, WhatsApp, LinkedIn) are managed under{" "}
        <Link to="/settings/connections" className="text-indigo-700 hover:underline font-medium">
          Settings → Connections
        </Link>
        .
      </p>
    </div>
  );
}

function sourceActionLabel(source, comingSoon, canManage) {
  if (comingSoon) return "Coming soon";
  if (source.action_label) return source.action_label;
  if (canManage) return "Manage";
  if (source.configured) return "Continue setup";
  return "Set up";
}

function SourceCard({ source, onConnect }) {
  const ui = SOURCE_ICONS[source.id] || { Icon: FileText, color: "bg-slate-500" };
  const { Icon } = ui;
  const docs = source.documents ?? 0;
  const lastSync = formatDateTime(source.last_sync);
  const isUpload = source.id === "custom" || source.id === "file_upload";
  const isGoogleDrive = source.id === "google_drive";
  // Temporary UI disable for Google Drive — integration remains fully implemented
  // (handleConnect + CloudImportWizard). Flip to false to restore the Manage button.
  const GOOGLE_DRIVE_UI_COMING_SOON = true;
  const comingSoon = source.coming_soon
    ?? (isGoogleDrive ? GOOGLE_DRIVE_UI_COMING_SOON : null)
    ?? (!isUpload && !isGoogleDrive && !source.connected);
  const canManage = isGoogleDrive
    ? Boolean(source.connection_id)
    : source.connected;
  const actionLabel = sourceActionLabel(source, comingSoon, canManage);

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 min-w-0 overflow-hidden" data-testid={`kb-source-card-${source.id}`}>
      <div className="flex items-start gap-3 min-w-0">
        <span className={cn("h-10 w-10 rounded-md text-white flex items-center justify-center shrink-0", ui.color)}>
          <Icon size={18} />
        </span>
        <div className="min-w-0 flex-1 overflow-hidden">
          <div
            className="text-[13.5px] font-semibold text-slate-900 truncate"
            title={source.name || undefined}
          >
            {source.name}
          </div>
          <div className="text-[11px] text-slate-500 line-clamp-2 break-words">{source.description}</div>
        </div>
        <SourceStatusBadge status={source.status} label={source.status_label} />
      </div>
      {source.identity && (
        <div className="mt-2 text-[11px] text-emerald-700 truncate" title={source.identity}>
          {source.identity}
        </div>
      )}
      <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
        <div>
          <div className="text-slate-500">Documents</div>
          <div className="font-semibold text-slate-800 tabular-nums">{docs}</div>
        </div>
        <div>
          <div className="text-slate-500">Last sync</div>
          <div className="font-semibold text-slate-800">{lastSync}</div>
        </div>
      </div>
      <Button
        variant="outline"
        size="sm"
        className="mt-3 w-full h-10 sm:h-8 text-[11.5px]"
        disabled={comingSoon}
        onClick={() => onConnect(source)}
      >
        {actionLabel}
      </Button>
    </div>
  );
}

SourceCard.propTypes = {
  source: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    status: PropTypes.string,
    status_label: PropTypes.string,
    identity: PropTypes.string,
    documents: PropTypes.number,
    last_sync: PropTypes.string,
    coming_soon: PropTypes.bool,
    connected: PropTypes.bool,
    connection_id: PropTypes.string,
    configured: PropTypes.bool,
    action_label: PropTypes.string,
  }).isRequired,
  onConnect: PropTypes.func.isRequired,
};

export function KnowledgeOverview() {
  const navigate = useNavigate();
  const assistantName = useAssistantName();
  const customMgrRef = useRef(null);
  const { overview, loading, error, apiIncomplete, refresh } = useBrainOverview();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get("connected");
    const oauthError = params.get("error");
    if (connected === "google_drive") {
      toast.success("Google Drive connected", {
        description: params.get("mock") ? "Development mock account linked." : "You can browse and import files now.",
      });
      customMgrRef.current?.openCloudImport("google_drive", { connected: true });
      refresh({ silent: true }).catch(() => { });
    }
    if (oauthError) {
      const messages = {
        access_denied: "Google sign-in was cancelled",
        unknown_state: "Sign-in session expired — try again",
        token_exchange_failed: "Could not complete Google sign-in",
      };
      toast.error(messages[oauthError] || `Google sign-in failed (${oauthError})`);
    }
    if (connected || oauthError) {
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [refresh]);

  const handleConnect = (source) => {
    if (source.id === "voice_memos") {
      if (BRAIN_EXTENDED_FEATURES_ENABLED) navigate("/brain/voice-memos");
      return;
    }
    if (source.id === "meeting_recordings") {
      toast.message("Meeting recordings — coming soon");
      return;
    }
    if (source.id === "custom" || source.id === "file_upload") {
      if (source.connected) {
        customMgrRef.current?.openManage(null);
      } else {
        customMgrRef.current?.openSetup(null);
      }
      return;
    }
    if (source.id === "google_drive") {
      customMgrRef.current?.openCloudImport("google_drive", {
        connected: Boolean(source.connection_id),
      });
      return;
    }
    toast.message(`${source.name} connector is not available yet`, {
      description: "Use File uploads to add PDFs and docs today. Repository connectors ship in a later release.",
    });
  };

  const deferredKnowledgeSources = [
    BRAIN_EXTENDED_FEATURES_ENABLED
      ? { ...DEFERRED_KNOWLEDGE_SOURCES.voice_memos, coming_soon: false, action_label: "Browse memos" }
      : { ...DEFERRED_KNOWLEDGE_SOURCES.voice_memos, coming_soon: true },
    { ...DEFERRED_KNOWLEDGE_SOURCES.meeting_recordings, coming_soon: true },
  ];

  const showEmptyBanner = !loading && !error && overview.connected_count === 0 && !overview.has_knowledge;

  return (
    <div className="flex flex-col flex-1 min-h-0 h-full min-w-0 overflow-y-auto overflow-x-hidden scrollbar-thin">
      <PageHeader
        title={`What ${assistantName} reads`}
        description={`Drop in docs, call recordings, meeting notes — ${assistantName} remembers everything for you.`}
        actions={
          <Button asChild variant="outline" size="sm" className="h-10 sm:h-8 w-full sm:w-auto">
            <Link to="/brain/playground"><Sparkles size={14} /> Test what {assistantName} knows</Link>
          </Button>
        }
      />
      <BrainNav />

      <div className="p-4 sm:p-6 space-y-5 min-w-0">
        {loading && (
          <div className="flex items-center gap-2 text-[13px] text-slate-500 py-8 justify-center">
            <Loader2 size={16} className="animate-spin text-indigo-600" />
            Loading knowledge source status…
          </div>
        )}

        {apiIncomplete && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-2 text-[13px] text-amber-900">
            <AlertCircle size={18} className="shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold">Brain upload API is outdated</div>
              <p className="mt-0.5 text-amber-800">
                The backend is missing knowledge source routes. Restart it from the project root:
                {" "}
                <code className="text-[11px] bg-amber-100/80 px-1 py-0.5 rounded break-all">cd backend; python -m uvicorn server:app --reload --port 8001</code>
              </p>
            </div>
          </div>
        )}

        {error && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 flex items-start gap-2 text-[13px] text-rose-800">
            <AlertCircle size={18} className="shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold">Couldn&apos;t load Brain status</div>
              <div className="mt-0.5">{error}</div>
              <Button size="sm" variant="outline" className="h-7 mt-2 text-[11px]" onClick={refresh}>
                Retry
              </Button>
            </div>
          </div>
        )}

        {!loading && (
          <>
            {BRAIN_EXTENDED_FEATURES_ENABLED && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                <KpiTile label="Documents" value={(overview.kpis?.documents ?? 0).toLocaleString()} />
                <KpiTile label="Chunks" value={(overview.kpis?.chunks ?? 0).toLocaleString()} />
                <KpiTile label="Embeddings" value={(overview.kpis?.embeddings ?? 0).toLocaleString()} hint={overview.has_knowledge ? "3072-dim" : "—"} />
                <KpiTile label="Vector size" value={overview.kpis?.vector_size_mb ? `${overview.kpis.vector_size_mb} MB` : "—"} />
                <KpiTile label="Last sync" value={formatDateTime(overview.kpis?.last_sync)} hint={overview.has_knowledge ? "auto" : undefined} />
              </div>
            )}

            {(showEmptyBanner || (error && overview.connected_count === 0)) && <BrainEmptyBanner />}

            <CustomSourceManager
              ref={customMgrRef}
              sources={overview.customSources}
              onChanged={(opts) => refresh(opts)}
              headless
            />

            <div className={cn("flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between pt-2", !BRAIN_EXTENDED_FEATURES_ENABLED && "pt-0")}>
              <div className="text-[13px] font-semibold text-slate-900">Knowledge sources</div>
              {BRAIN_EXTENDED_FEATURES_ENABLED && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-10 sm:h-8 text-[11.5px] w-full sm:w-auto"
                  disabled={overview.connected_count === 0}
                  onClick={() => toast.message("Nothing to sync yet", { description: "Connect a knowledge source first." })}
                >
                  <RefreshCw size={11} /> Sync all
                </Button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 min-w-0">
              {overview.knowledgeSources.map((s) => (
                <SourceCard key={s.id} source={s} onConnect={handleConnect} />
              ))}

              {deferredKnowledgeSources.map((s) => (
                <SourceCard key={s.id} source={s} onConnect={handleConnect} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function DocumentsLibrary() {
  const navigate = useNavigate();
  const assistantName = useAssistantName();
  const [search, setSearch] = useState("");
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.info("[brain] Brain documents request started", { endpoint: "/api/brain/documents" });
    }
    brainApi.documents()
      .then((r) => {
        if (process.env.NODE_ENV === "development") {
          // eslint-disable-next-line no-console
          console.info("[brain] Brain documents response returned", { total: r?.total ?? 0 });
        }
        if (!cancelled) setDocuments(Array.isArray(r?.documents) ? r.documents : []);
      })
      .catch((e) => {
        if (process.env.NODE_ENV === "development") {
          // eslint-disable-next-line no-console
          console.warn("[brain] Brain documents request failed", { status: e?.status, message: e?.message });
        }
        if (!cancelled) setDocuments([]);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const filtered = documents.filter((d) => {
    if (!search) return true;
    const q = search.toLowerCase();
    const name = (d.original_filename || d.title || "").toLowerCase();
    const source = (d.source_type_label || d.source_name || d.source || "").toLowerCase();
    return name.includes(q) || source.includes(q);
  });

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center gap-2 text-[13px] text-slate-500 py-16">
          <Loader2 size={16} className="animate-spin" /> Loading documents…
        </div>
      );
    }
    if (filtered.length === 0) {
      return (
        <div className="max-w-md mx-auto text-center py-12 sm:py-16 px-2" data-testid="docs-empty">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-500 mb-4">
            <FileText size={22} />
          </div>
          <h3 className="text-[18px] font-semibold text-slate-900">No documents indexed yet</h3>
          <p className="mt-2 text-[13px] text-slate-600">
            Connect a knowledge source on the Brain overview page, or upload files once ingestion is enabled.
          </p>
          <Button size="sm" className="mt-4 h-10 sm:h-8 w-full sm:w-auto" onClick={() => navigate("/brain")}>Go to Brain</Button>
        </div>
      );
    }
    return (
      <>
        <div className="space-y-2 md:hidden" data-testid="docs-mobile-list">
          {filtered.map((d) => (
            <div key={d.id} className="rounded-xl border border-slate-200 bg-white p-3 min-w-0">
              <div className="text-[13px] font-medium text-slate-900 break-words">{d.original_filename || d.title || "—"}</div>
              <div className="mt-1 text-[11.5px] text-slate-600 truncate">{d.source_type_label || d.source_name || d.source || "—"}</div>
              <div className="mt-1.5 text-[11.5px] text-slate-600">
                <span className={d.status === "failed" ? "text-rose-700" : ""}>{d.status}</span>
                {d.status === "failed" && d.error_message && (
                  <div className="text-[10.5px] text-rose-600 mt-0.5 break-words" title={d.error_message}>
                    {d.error_message}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead className="bg-slate-50/60 sticky top-0">
              <tr className="text-left text-[10.5px] uppercase tracking-wider text-slate-500">
                <th className="px-4 py-2 font-medium">File</th>
                <th className="px-4 py-2 font-medium">Source</th>
                <th className="px-4 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((d) => (
                <tr key={d.id} className="border-t border-slate-100">
                  <td className="px-4 py-2.5 font-medium text-slate-900">{d.original_filename || d.title || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-700">{d.source_type_label || d.source_name || d.source || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-600">
                    <span className={d.status === "failed" ? "text-rose-700" : ""}>{d.status}</span>
                    {d.status === "failed" && d.error_message && (
                      <div className="text-[10.5px] text-rose-600 mt-0.5 truncate max-w-xs" title={d.error_message}>
                        {d.error_message}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  };

  return (
    <div className="flex flex-col h-full min-w-0 overflow-hidden">
      <PageHeader title="Your documents" description={`Everything ${assistantName} has read for you.`} breadcrumbs={["Brain", "Your documents"]} />
      <BrainNav />
      <div className="px-4 sm:px-6 py-3 border-b border-slate-200 bg-white shrink-0">
        <div className="relative max-w-md w-full">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search documents…" className="h-11 sm:h-9 pl-7 w-full min-w-0" data-testid="docs-search" />
        </div>
      </div>
      <div className="flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden scrollbar-thin p-4 sm:p-6">
        {renderContent()}
      </div>
    </div>
  );
}

export function ChunkingConfig() {
  const assistantName = useAssistantName();
  const [strat, setStrat] = useState("md");
  const [chunkSize, setChunkSize] = useState(512);
  const [overlap, setOverlap] = useState(64);
  const [sample, setSample] = useState("# Acme Discount Policy v3\n\nStandard discounts up to 12% are auto-approved.\nAEs may grant up to 15% with deal-desk review.\nDiscounts above 15% require CRO approval.\n\n## Multi-year commits\n3yr deals receive an additional 5% on top of the standard volume discount.");
  const chunks = sample.split(/\n\n+/);
  return (
    <div className="flex flex-col h-full min-w-0 overflow-y-auto overflow-x-hidden scrollbar-thin">
      <PageHeader title={`How ${assistantName} remembers`} description={`${assistantName} breaks down everything you share so the right bit can be recalled at the right moment.`} breadcrumbs={["Brain", `How ${assistantName} remembers`]} />
      <BrainNav />
      <div className="p-4 sm:p-6 space-y-5 min-w-0">
        <div className="rounded-lg border border-slate-200 bg-slate-50/60 px-3 sm:px-4 py-3 text-[12.5px] text-slate-600">
          These settings apply after you connect a knowledge source and index documents. Until then, defaults are shown for preview only.
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[
            { step: 1, icon: BookOpen, title: "Read", desc: `${assistantName} reads every doc, transcript, memo and webpage you provide — cover to cover.`, color: "from-indigo-50 to-white", iconColor: "text-indigo-600" },
            { step: 2, icon: Database, title: "Break into pieces", desc: "Long text is split into bite-sized pieces. Each piece keeps just enough context to stand on its own.", color: "from-emerald-50 to-white", iconColor: "text-emerald-600" },
            { step: 3, icon: Sparkles, title: "Find when needed", desc: `When you (or a customer) asks a question, ${assistantName} pulls the right pieces — and only those — to answer.`, color: "from-amber-50 to-white", iconColor: "text-amber-600" },
          ].map((s) => {
            const I = s.icon;
            return (
              <div key={s.step} className={cn("rounded-xl border border-slate-200 bg-gradient-to-br p-4", s.color)}>
                <div className="flex items-center gap-2 mb-1.5 min-w-0">
                  <span className="h-6 w-6 rounded-full bg-white text-[11px] font-bold text-slate-700 ring-1 ring-slate-200 flex items-center justify-center shrink-0">{s.step}</span>
                  <I size={14} className={cn(s.iconColor, "shrink-0")} />
                  <div className="text-[13px] font-semibold text-slate-900 truncate">{s.title}</div>
                </div>
                <div className="text-[11.5px] text-slate-600 leading-relaxed">{s.desc}</div>
              </div>
            );
          })}
        </div>

        {BRAIN_EXTENDED_FEATURES_ENABLED && (
          <details className="rounded-xl border border-slate-200 bg-white" data-testid="advanced-memory">
            <summary className="px-3 sm:px-4 py-3 cursor-pointer text-[12.5px] font-semibold text-slate-800 flex items-start sm:items-center gap-2 select-none hover:bg-slate-50 min-w-0">
              <Cog size={12} className="shrink-0 mt-0.5 sm:mt-0" />
              <span className="min-w-0 inline-flex flex-col sm:flex-row sm:items-baseline sm:gap-1">
                <span>Advanced settings</span>
                <span className="text-[10.5px] text-slate-400 font-normal">— chunk size, overlap, embedding model, vector store</span>
              </span>
            </summary>
            <div className="p-4 pt-2 border-t border-slate-100 space-y-5">
              <div>
                <div className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500 mb-2">Strategy</div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2">
                  {chunkingStrategies.map((s) => (
                    <button key={s.id} onClick={() => setStrat(s.id)} className={cn("text-left rounded-lg border p-3 transition-colors", strat === s.id ? "border-indigo-500 bg-indigo-50/40 ring-1 ring-indigo-200" : "border-slate-200 bg-white hover:border-slate-300")} data-testid={`chunking-${s.id}`}>
                      <div className="text-[12.5px] font-semibold text-slate-900">{s.name}</div>
                      <div className="text-[11px] text-slate-500">{s.description}</div>
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-4">
                  <div>
                    <div className="flex items-center justify-between"><Label className="text-[12px]">Chunk size (tokens)</Label><span className="font-mono text-[11px] text-slate-700">{chunkSize}</span></div>
                    <Slider value={[chunkSize]} min={128} max={2048} step={64} onValueChange={([v]) => setChunkSize(v)} className="mt-2" />
                  </div>
                  <div>
                    <div className="flex items-center justify-between"><Label className="text-[12px]">Overlap (tokens)</Label><span className="font-mono text-[11px] text-slate-700">{overlap}</span></div>
                    <Slider value={[overlap]} min={0} max={256} step={8} onValueChange={([v]) => setOverlap(v)} className="mt-2" />
                  </div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-2">
                  <div className="text-[12px] font-semibold text-slate-900">Live preview</div>
                  <textarea value={sample} onChange={(e) => setSample(e.target.value)} rows={6} className="w-full rounded-md border border-slate-200 p-2 text-[12px] font-mono" />
                  <div className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500 pt-2">Would produce {chunks.length} chunks</div>
                </div>
              </div>
            </div>
          </details>
        )}
      </div>
    </div>
  );
}

export function EmbeddingsStatus() {
  return <ChunkingConfig />;
}

export function RagPlayground() {
  const navigate = useNavigate();
  const assistantName = useAssistantName();
  const { overview, loading } = useBrainOverview();
  const [query, setQuery] = useState("");
  const [topK, setTopK] = useState(5);
  const [threshold, setThreshold] = useState(0.15);
  const [reranker, setReranker] = useState(true);
  const [answer, setAnswer] = useState("");
  const [answerHtml, setAnswerHtml] = useState("");
  const [ragStatus, setRagStatus] = useState(null);
  const [grounded, setGrounded] = useState(false);
  const [searching, setSearching] = useState(false);

  const runSearch = async () => {
    if (!query.trim()) return;
    setSearching(true);
    setAnswer("");
    setAnswerHtml("");
    setRagStatus(null);
    try {
      const res = await brainApi.playgroundAnswer({
        query: query.trim(),
        limit: BRAIN_EXTENDED_FEATURES_ENABLED ? topK : 12,
        min_score: BRAIN_EXTENDED_FEATURES_ENABLED ? threshold : undefined,
      });
      setAnswer(res.answer || "");
      setAnswerHtml(res.answer_html || "");
      setRagStatus(res.status || null);
      setGrounded(Boolean(res.grounded));
    } catch (e) {
      toast.error(e.message || "Search failed");
      setAnswer("");
      setAnswerHtml("");
    } finally {
      setSearching(false);
    }
  };

  if (!loading && !overview.has_knowledge) {
    return (
      <div className="flex flex-col h-full min-w-0 overflow-hidden">
        <PageHeader title={`Test what ${assistantName} knows`} description={`Ask ${assistantName} anything. Answers are generated only from your indexed Knowledge Base — no web search or outside knowledge.`} breadcrumbs={["Brain", `Test what ${assistantName} knows`]} />
        <BrainNav />
        <div className="flex-1 flex items-center justify-center p-4 sm:p-6">
          <div className="max-w-md text-center px-1" data-testid="playground-empty">
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 mb-4">
              <Search size={22} />
            </div>
            <h3 className="text-[18px] font-semibold text-slate-900">{assistantName} needs some knowledge first</h3>
            <p className="mt-2 text-[13px] text-slate-600">
              Connect a knowledge source and index documents before running RAG queries.
            </p>
            <Button size="sm" className="mt-4 h-10 sm:h-8 w-full sm:w-auto" onClick={() => navigate("/brain")} data-testid="playground-empty-brain">
              Go to Brain
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-w-0 overflow-hidden">
      <PageHeader title={`Test what ${assistantName} knows`} description={`Ask ${assistantName} anything. Answers are generated only from your indexed Knowledge Base — no web search or outside knowledge.`} breadcrumbs={["Brain", `Test what ${assistantName} knows`]} />
      <BrainNav />
      <div className="flex-1 min-h-0 min-w-0 grid grid-cols-1 lg:grid-cols-[380px_1fr] overflow-y-auto lg:overflow-hidden">
        <aside className="border-b lg:border-b-0 lg:border-r border-slate-200 bg-white p-4 overflow-visible lg:overflow-y-auto scrollbar-thin space-y-4 shrink-0">
          <div>
            <Label className="text-[12px]">Query</Label>
            <textarea value={query} onChange={(e) => setQuery(e.target.value)} rows={3} className="mt-1 w-full min-w-0 rounded-md border border-slate-200 p-2.5 text-[13px]" data-testid="rag-query" placeholder="Ask a question…" />
          </div>
          <div>
            <Label className="text-[12px]">Sources</Label>
            <div className="mt-1 text-[11px] text-slate-500">
              {overview.connected_count === 0
                ? "No connected sources"
                : `${overview.connected_count} connected source(s)`}
            </div>
          </div>
          {BRAIN_EXTENDED_FEATURES_ENABLED && (
            <>
              <div>
                <div className="flex items-center justify-between"><Label className="text-[12px]">Top K</Label><span className="font-mono text-[11px]">{topK}</span></div>
                <Slider value={[topK]} min={1} max={20} step={1} onValueChange={([v]) => setTopK(v)} className="mt-2" />
              </div>
              <div>
                <div className="flex items-center justify-between"><Label className="text-[12px]">Similarity threshold</Label><span className="font-mono text-[11px]">{threshold.toFixed(2)}</span></div>
                <Slider value={[threshold]} min={0} max={1} step={0.01} onValueChange={([v]) => setThreshold(v)} className="mt-2" />
              </div>
              <div className="flex items-center justify-between"><Label className="text-[12px]">Reranker</Label><Switch checked={reranker} onCheckedChange={setReranker} /></div>
            </>
          )}
          <Button className="w-full h-11 sm:h-10" disabled={searching || !query.trim()} onClick={runSearch} data-testid="rag-run">
            {searching ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
            {searching ? " Searching…" : " Run query"}
          </Button>
        </aside>
        <div className="p-4 sm:p-6 overflow-y-auto scrollbar-thin min-w-0 lg:min-h-0">
          {answer ? (
            <div
              className={cn(
                "rounded-xl border p-4 sm:p-5 min-w-0 w-full self-start",
                grounded ? "border-indigo-200 bg-indigo-50/40" : "border-amber-200 bg-amber-50/50",
              )}
              data-testid="rag-answer"
            >
              {answerHtml ? (
                <div
                  className="brain-playground-answer"
                  data-testid="rag-answer-html"
                  dangerouslySetInnerHTML={{ __html: answerHtml }}
                />
              ) : (
                <div className="brain-playground-answer brain-playground-answer--plain">{answer}</div>
              )}
            </div>
          ) : (
            <div className="text-[13px] text-slate-500 text-center py-10 sm:py-16 min-h-[12rem] flex items-center justify-center">
              {query && ragStatus === "insufficient_kb"
                ? null
                : "Ask a question to see the Knowledge Base answer here."}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function BrainDocumentsRoute() {
  if (!BRAIN_EXTENDED_FEATURES_ENABLED) return <Navigate to="/brain" replace />;
  return <DocumentsLibrary />;
}
